import {Box, BoxProps, CircularProgress, Typography} from "@mui/material";
import {FC, useEffect, useState, useRef} from "react";
import {makeStyles} from "tss-react/mui";
import {ICourse} from "@buildwitt/shapeshift-types/ICourse";
import {IAPIDrivenChildrenFunction} from "@buildwitt/shapeshift-common/types/ICommon";
import {LibrarySearch} from "@buildwitt/shapeshift-common/v1/library";
import {ILibrarySearch} from "@buildwitt/shapeshift-types";
import CustomLibraryCarousels from "../../components/course/CustomLibraryCarousels";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useCourseSearch from "../../hooks/useCourseSearch";
import {IconCourseLibrary} from "@/icons/IconCourseLibrary";

interface IPropsCustom {
	data?: ICourse[],
	children?: IAPIDrivenChildrenFunction<ICourse[] | undefined>,
}

export type IProps = Omit<BoxProps, "children"> & IPropsCustom;

const useStyles = makeStyles()((theme) => ({
	library: {
		overflow: "visible"
	},
	title: {
		margin: "10px",
		fontSize: "34px",
		lineHeight: "35px",
		[theme.breakpoints.down("sm")]: {
			fontSize: "35px"
		}
	},
	searchWrapper: {
		marginBottom: "10px",
		display: "flex",
		boxSizing: "border-box",
		background: palette.black,
		flexDirection: "column",
		gap: "10px",
		width: "100%",
		["& [class*='searchBox-searchBox']"]: {
			display: "flex",
			justifyContent: "center",
			flexDirection: "column",
			paddingLeft: "15px"
		},
		["& [class*='librarySearch'] > *"]: {
			display: "none"
		},
		["& [class*='librarySearch'] [class*='MuiTextField']"]: {
			background: palette.grey10,
			border: `1px solid ${palette.grey05}`,
			height: "52px",
			borderRadius: "8px",
			"input": {
				color: theme.palette.common.white
			},
			"& ::placeholder": {
				opacity: "1",
				color: theme.palette.common.white
			},
			"svg": {
				fill: theme.palette.common.white
			},
			display: "flex"
		},
		[theme.breakpoints.up("sm")]: {
			marginTop: "-80px",
			padding: "32px 60px 40px 60px",
			maxWidth: "1420px",
			borderRadius: "20px",
			marginLeft: "auto",
			marginRight: "auto"
		},
		[theme.breakpoints.down("sm")]: {
			padding: "30px 20px 40px 20px",
			marginLeft: "0",
			marginRight: "0",
			["& [class*='librarySearch'] [class*='MuiTextField']"]: {
				border: "none",
				height: "52px",
				display: "flex"
			}
		}
	},
	search: {
		margin: 0
	},
	searchLabel: {
		color: theme.palette.common.white,
		fontWeight: 600,
		fontSize: "20px",
		[theme.breakpoints.down("sm")]: {
			fontSize: "16px"
		}
	},
	countText: {
		color: theme.palette.secondary.main
	},
	noneFoundContainer: {
		display: "flex",
		justifyContent: "center",
		color: theme.palette.text.primary,
		flexDirection: "column",
		gap: "15px",
		"svg": {
			width: "60px",
			height: "60px",
			"path": {
				fill: palette.greySolid
			}
		},
		"& div": {
			display: "flex",
			justifyContent: "center",
			flexDirection: "column"
		}
	},
	carouselsContainer: {
		display: "flex",
		flexDirection: "column",
		margin: "40px auto 90px auto",
		width: "100%",
		maxWidth: "1600px",
		boxSizing: "border-box",
		[theme.breakpoints.up("md")]: {
			padding: "0 40px"
		},
		[theme.breakpoints.between("sm", "md")]: {
			padding: "0 20px"
		},
		[theme.breakpoints.down("sm")]: {
			padding: "0 10px"
		}
	}
}));

export const CustomLibraryContent: FC<IProps> = ({className, data, children, ...props}) => {
	const {t} = useSSTranslation();
	const {classes, cx} = useStyles();
	const [searchValue, setSearchValue] = useState<ILibrarySearch>({searchTerm: "", includeEnrolled: true, includeGroups: false, includeCourses: true});

	const [coursesData, setCoursesData] = useState<any>(null);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [hasMoreData, setHasMoreData] = useState<boolean>(true);
	const [categoryPages, setCategoryPages] = useState<{ [key: string]: number }>({});
	const [categoryHasMore, setCategoryHasMore] = useState<{ [key: string]: boolean }>({});
	const [categoryData, setCategoryData] = useState<{ [key: string]: any[] }>({});
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const {courseData, apiResult, getCourseSearchData} = useCourseSearch();

	const coursesCategories = [
		"Job Training",
		"Personal Development",
		"Professional Development",
		"Safety",
		"Others"
	];

	const pageSize = 50;

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const getCategoryDisplayName = (category: string) => {
		const categoryMap: Record<string, string> = {
			"Job Training": "course_category_Job Training",
			"Personal Development": "course_category_Personal Development",
			"Professional Development": "course_category_Professional Development",
			"Safety": "course_category_Safety",
			"Others": "course_category_others"
		};

		const translationKey = categoryMap[category];
		return translationKey ? t(translationKey as any, category) : category;
	};

	useEffect(() => {
		// Reset pagination when search term changes
		setCurrentPage(1);
		setHasMoreData(true);
		setCategoryPages({});
		setCategoryHasMore({});

		// Clear previous timeout
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		// Set timeout for debounce
		debounceTimeoutRef.current = setTimeout(() => {
			// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
			const fetchCoursesData = async () => {
				try {
					await getCourseSearchData({
						page: 1,
						searchTerm: searchValue.searchTerm || "",
						pageSize
					});
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error("Error fetching courses:", error);
				}
			};
			fetchCoursesData();
		}, 400);

		// Cleanup timeout on unmount
		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [searchValue.searchTerm, getCourseSearchData]);

	// Handle course data from the hook
	useEffect(() => {
		try {
			if (courseData && !apiResult.isError) {
				const coursesArray = courseData.data || [];
				const currentPage = courseData.page || 1;

				// For initial search (page 1), set all data
				if (currentPage === 1) {
					setCoursesData(coursesArray);
					setTotalCount(courseData.totalCount || 0);
					setHasMoreData(coursesArray.length < (courseData.totalCount || 0));

					// Initialize category data
					const initialCategoryData: { [key: string]: any[] } = {};
					const initialCategoryHasMore: { [key: string]: boolean } = {};

					coursesCategories.forEach(category => {
						const categoryData = category === "Others"
							? coursesArray.filter((course: CourseWithContentType) => {
								// Include courses that don't have contentType or don't belong to defined categories
								const hasNoContentType = !course.contentType || course.contentType === "" || course.contentType === null;
								const belongsToDefinedCategory = ["Job Training", "Personal Development", "Professional Development", "Safety"].includes(course.contentType);
								return hasNoContentType || !belongsToDefinedCategory;
							})
							: coursesArray.filter((course: CourseWithContentType) => course.contentType === category);
						initialCategoryData[category] = categoryData;
						initialCategoryHasMore[category] = categoryData.length > 0;
					});

					setCategoryData(initialCategoryData);
					setCategoryHasMore(initialCategoryHasMore);
				}
			} else if (apiResult.isError) {
				// Reset data on error
				setCoursesData([]);
				setTotalCount(0);
				setHasMoreData(false);
				setCategoryData({});
				setCategoryHasMore({});
			}
		} catch (error) {
			// Reset data on error
			setCoursesData([]);
			setTotalCount(0);
			setHasMoreData(false);
			setCategoryData({});
			setCategoryHasMore({});
		}
	}, [courseData, apiResult.isError, apiResult.error]);

	// Handle additional data loading for specific categories
	useEffect(() => {
		if (courseData && !apiResult.isError && courseData.page > 1) {
			const coursesArray = courseData.data || [];
			const totalCount = courseData.totalCount || 0;

			// Add new data to existing courses data
			setCoursesData(prevData => {
				if (!prevData) return coursesArray;
				// Check for duplicates
				const existingIds = new Set(prevData.map((course: any) => course.id));
				const newData = coursesArray.filter((course: any) => !existingIds.has(course.id));
				const updatedData = [...prevData, ...newData];

				if (updatedData.length >= totalCount && totalCount > 0) {
					setCategoryHasMore(prev => {
						const updated = {...prev};
						coursesCategories.forEach(cat => {
							updated[cat] = false;
						});
						return updated;
					});
				}

				return updatedData;
			});

			// Update category data
			setCategoryData(prevCategoryData => {
				const updatedCategoryData = {...prevCategoryData};

				coursesCategories.forEach(category => {
					const categoryData = category === "Others"
						? coursesArray.filter((course: CourseWithContentType) => {
							// Include courses that don't have contentType or don't belong to defined categories
							const hasNoContentType = !course.contentType || course.contentType === "" || course.contentType === null;
							const belongsToDefinedCategory = ["Job Training", "Personal Development", "Professional Development", "Safety"].includes(course.contentType);
							return hasNoContentType || !belongsToDefinedCategory;
						})
						: coursesArray.filter((course: CourseWithContentType) => course.contentType === category);
					if (categoryData.length > 0) {
						const existingCategoryData = updatedCategoryData[category] || [];
						const existingIds = new Set(existingCategoryData.map((course: any) => course.id));
						const newCategoryData = categoryData.filter((course: any) => !existingIds.has(course.id));
						updatedCategoryData[category] = [...existingCategoryData, ...newCategoryData];
					}
				});
				return updatedCategoryData;
			});
		}
	}, [courseData, apiResult.isError]);

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const loadMoreDataForCategory = async (category: string) => {
		if (apiResult.isLoading) return;

		const totalCoursesGlobally = coursesData?.length || 0;
		const totalCountGlobally = totalCount || 0;

		if (totalCoursesGlobally >= totalCountGlobally) {
			setCategoryHasMore(prev => ({...prev, [category]: false}));
			return;
		}

		const currentCategoryPage = categoryPages[category] || 1;

		try {
			const nextPage = currentCategoryPage + 1;
			await getCourseSearchData({
				page: nextPage,
				searchTerm: searchValue.searchTerm || "",
				pageSize
			});
			// Update category page for tracking
			setCategoryPages(prev => ({...prev, [category]: nextPage}));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error loading more courses for category:", error);
			// On error, mark only this category as done
			setCategoryHasMore(prev => ({...prev, [category]: false}));
		}
	};

	type CourseWithContentType = ICourse & {
		contentType: string;
		userProgressPercentage?: number;
		totalDurationSeconds?: number;
		watchedDurationSeconds?: number;
		remainingDurationSeconds?: number;
	};

	return (
		<Box className={cx(classes.library, className)} {...props}>
			<Box className={classes.searchWrapper}>
				<Typography className={classes.searchLabel}>{t("search_courses", "Search courses")} {apiResult.isLoading ? <CircularProgress size={20} /> : totalCount > 0 ? <span className={classes.countText}>{`(${totalCount})`}</span> : null}</Typography>

				<LibrarySearch
					className={classes.search}
					value={searchValue}
					onChange={setSearchValue}
					textFieldProps={{
						inputProps: {
							placeholder: t("search_placeholder", "Course Title, Lesson Title, Author, Lesson Description")

						}
					}}
				/>
			</Box>
			<Box className={classes.carouselsContainer}>
				{!apiResult.isLoading && coursesData && Array.isArray(coursesData) && coursesData.length === 0 ? (
					<Box className={classes.noneFoundContainer}>
						<IconCourseLibrary />
						<Box><Typography variant="h5">{t("no_courses_found", "No Courses Found")}</Typography></Box>

					</Box>
				) : (
					coursesData && Array.isArray(coursesData) &&
					coursesCategories
						.filter(category => {
							const categoryDataArray = categoryData[category] || [];
							return categoryDataArray.length > 0;
						})
						.map(category => {
							// Get courses for this category
							const searchCourses = categoryData[category] || [];

							// Enrich courses with additional data from API
							const enrichedCourses = searchCourses.map(searchCourse => {
								try {
									return {
										...searchCourse,
										// Map API duration fields to expected format
										lessonDuration: searchCourse.totalDurationSeconds
											? Math.floor(searchCourse.totalDurationSeconds / 60)
											: 0,
										// Ensure all new API fields are available
										userProgressPercentage: searchCourse.userProgressPercentage || 0,
										totalDurationSeconds: searchCourse.totalDurationSeconds || 0,
										watchedDurationSeconds: searchCourse.watchedDurationSeconds || 0,
										remainingDurationSeconds: searchCourse.remainingDurationSeconds || 0,
										courseProgress: searchCourse.courseProgress || 0
									};
								} catch (error) {
									// Return course with safe defaults
									return {
										...searchCourse,
										lessonDuration: 0,
										userProgressPercentage: 0,
										totalDurationSeconds: 0,
										watchedDurationSeconds: 0,
										remainingDurationSeconds: 0,
										courseProgress: 0
									};
								}
							});

							return (
								<CustomLibraryCarousels
									key={category}
									contentType={getCategoryDisplayName(category)}
									data={enrichedCourses}
									onLoadMore={loadMoreDataForCategory}
									hasMoreData={categoryHasMore[category] !== false}
									isLoading={apiResult.isLoading}
								/>
							);
						})
				)}
			</Box>
		</Box>
	)
}

export default CustomLibraryContent;
